<template>
  <div
    class="media-blocks critical-component-clear"
    :style="{
      '--section-spacing': spacing,
      '--section-spacing-l': spacingDesktop
    }"
  >
    <component
      :is="getBlockComponent(block)"
      v-for="(block, index) of blocks"
      ref="block"
      :key="`media-blocks-${index}`"
      class="media-blocks__block"
      :class="{
        video: block.content_type === 'video'
      }"
      :data-has-mobile-video="block.content_type === 'video' && block.mobile_video?.sources?.length > 0"
      :data-oldschool-video="block.content_type === 'video' && block.oldschool_video === true"
    >
      <video-player
        v-if="block.content_type === 'video'"
        block-class="media-blocks"
        :src="getVideoSource(block)"
        overlay
        :autoplay="isMobile ? block.mobile_autoplay : block.desktop_autoplay"
        loop
        muted
        :poster="getVideoPoster(block)"
        :preload="videoPreloadValue(autoplayOverride)"
        :controls="isMobile ? block.mobile_controls : block.desktop_controls"
      />

      <picture class="media-blocks__picture" v-else-if="block.content_type === 'image'">
        <template v-if="block.mobile_image">
          <source
            media="(max-width: 1023px)"
            :srcset="block.mobile_image.src"
            sizes="100vw"
            fetchpriority="high"
            importance="high"
          />
          <source
            media="(min-width: 1024px)"
            :srcset="block.image.src"
            sizes="100vw"
            fetchpriority="low"
          />
          <img
            :src="block.mobile_image.src"
            :alt="block.mobile_image.alt"
            class="images__image"
            :class="$blockClass(blockClass, 'image')"
            :loading="block.lazy_load ? 'lazy' : 'eager'"
            decoding="async"
            sizes="100vw"
            fetchpriority="high"
            importance="high"
            preload="true"
            :style="{
              'object-position': isMobile ? (block.mobile_image.presentation?.focal_point || 'center') : (block.image.presentation?.focal_point || 'center'),
              'width': block.mobile_image.width,
              'height': block.mobile_image.height
            }"
          />
        </template>
        <responsive-image
          v-else
          block-class="media-blocks"
          :image="block.image"
          :lazyload="block.lazy_load"
          :loaded-animation="false"
          min-max="2048-4096"
          :padding="false"
          :ratio="false"
          sizes="100vw"
        />
      </picture>

      <div
        v-if="block.content_type === 'image' && blockHasOverlay(block)"
        class="media-blocks__overlay"
      >
        <span
          v-if="block.subtitle"
          class="
            media-blocks__subtitle
            text-body-xs-label
            text-body-s-label-desktop
          "
          v-text="block.subtitle"
        />

        <component
          :is="liquid.sectionIndex === 0 && index === 0 ? 'h1' : 'p'"
          v-if="block.title"
          class="media-blocks__title text-heading-l text-heading-2xl-desktop"
        >
          <span v-text="block.title" />
        </component>

        <div
          v-if="block.content"
          class="media-blocks__content"
        >
          <p
            class="media-blocks__content-text text-body-s text-body-m-desktop"
            v-text="block.content"
          />
        </div>

        <btn
          v-if="block.url && block.button"
          :label="block.button"
          block-class="media-blocks"
          parent-hover
          :url="block.url"
        />
      </div>
    </component>
  </div>
</template>

<script>

/**
 * Vue: Media blocks (media-blocks)
 * -----------------------------------------------------------------------------
 * Full bleed image or video blocks.
 *
 * @param {Object[]} blocks - The list of media blocks.
 * @param {String} blocks[].mobile_video - Mobile video source content.
 * @param {String} blocks[].desktop_video - Desktop video source content.
 * @param {Object} blocks[].image - The image object.
 * @param {String} blocks[].subtitle - Short text displayed above the title.
 * @param {String} blocks[].title - The block title.
 * @param {String} blocks[].content - Longer content displayed below the title.
 * @param {String} blocks[].button - The button label, hidden if no link is
 * provided.
 * @param {Boolean} blocks[].lazyLoad - Lazy load the image.
 * @param {Boolean} blocks[].mobile_autoplay - Mobile autoplay the video.
 * @param {Boolean} blocks[].desktop_autoplay - Desktop autoplay the video.
 * @param {String} blocks[].url - The block link, disabled if video is active.
 * @param {String} spacing - Custom spacing value.
 * @param {String} spacingDesktop - Custom spacing value for desktop.
 * @param {Number} liquid.sectionIndex - Section's 0 based index.
 * @param {Boolean|Null} autoplayOverride - Overrides the block level
 * autoplay and autoplays all videos in the section.
 *
 */
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import VideoPlayer from '~async/video-player/video-player'

import { setIntersectionObserver, videoPreloadValue } from '~/helpers/general'
import classes from '~/config/classes'
import { mapState } from 'vuex'

const transitions = {
  block: {
    in: 1,
    out: 0.8,
  },
}

export default {
  name: 'MediaBlocksNew',

  components: {
    Btn,
    ResponsiveImage,
    VideoPlayer,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },

    spacing: {
      type: String,
      default: '',
    },

    spacingDesktop: {
      type: String,
      default: '',
    },

    autoplayOverride: {
      type: [Boolean, null],
      default: null,
    },

    liquid: {
      type: Object,
      default: () => ({
        sectionIndex: 0,
        sectionContent: '',
      }),
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.$refs.block?.forEach((element) => {
      setIntersectionObserver({
        callback: this.handleBlockIntersection,
        rootMargin: '-60px 0px 0px 0px',
        element,
        threshold: [transitions.block.out, transitions.block.in]
      })
    })
  },

  methods: {
    videoPreloadValue,

    /**
     * Handles a block's intersection event.
     * @param {Array} entries - A list of intersection events.
     * @param {Object} observer - The observer instance.
     */
     handleBlockIntersection(entries) {
      entries.forEach((entry) => {
        if (
          !entry.isIntersecting &&
          entry.intersectionRatio <= transitions.block.out
        ) {
          entry.target.classList.remove(classes.active)
          return
        }

        entry.target.classList.add(classes.active)
      })
    },

    sortSources(source) {
      return source.sort((a, b) => b.width - a.width)
    },

    /**
     * Returns the block component type.
     * @param {Object} block - The block object.
     * @returns {String}
     */
    getBlockComponent(block) {
      if (!block.url || block.mobile_video || block.desktop_video) {
        return 'div'
      }

      return 'a'
    },

    /**
     * Returns if the given block has an overlay.
     * @param {Object} block - The block object.
     * @returns {Boolean}
     */
    blockHasOverlay(block) {
      return block.subtitle ||
        block.title ||
        block.content ||
        (block.url && block.button)
    },

    /**
     * Determines whether the video should be auto-played based on
     * the block settings and if there is a section autoplay override
     * in place.
     * @param {Object} block - Block data.
     * @returns {Boolean}
     */
    autoplayVideo(block) {
      if (this.autoplayOverride !== null) {
        return this.autoplayOverride
      }

      if (block.autoplay) {
        return block.autoplay
      }

      return false
    },

    /**
     * Get video source
     * @param {Object} block - Block object.
     * @return {Object|null}
     */
    getVideoSource(block) {
      // For mobile devices
      if (this.isMobile) {
        // If mobile video exists and has a valid source
        if (block.mobile_video?.sources?.length > 0) {
          const mobileSource = block.mobile_video.sources.find(({height}) => height === 1080)
          if (mobileSource) {
            return mobileSource.url
          }
        }

        // Fallback to desktop video if mobile video is not available
        if (block.desktop_video?.sources?.length > 0) {
          const desktopSource = block.desktop_video.sources.find(({height}) => height === 1080)
          if (desktopSource) {
            return desktopSource.url
          }
        }
      }

      // For desktop devices
      if (block.desktop_video?.sources?.length > 0) {
        const desktopSource = block.desktop_video.sources.find(({height}) => height === 1080)
        if (desktopSource) {
          return desktopSource.url
        }
      }

      // Fallback to mobile video if desktop video is not available
      if (block.mobile_video?.sources?.length > 0) {
        const mobileSource = block.mobile_video.sources.find(({height}) => height === 1080)
        if (mobileSource) {
          return mobileSource.url
        }
      }

      return null
    },

    /**
     * Get video poster.
     * @param {Object} block - Block object.
     * @return {Object|null}
     */
    getVideoPoster(block) {
      // For mobile devices
      if (this.isMobile) {
        if (block.mobile_video?.preview_image?.src) {
          return block.mobile_video.preview_image.src
        }

        // Fallback to desktop poster
        if (block.desktop_video?.preview_image?.src) {
          return block.desktop_video.preview_image.src
        }
      }

      // For desktop devices
      if (block.desktop_video?.preview_image?.src) {
        return block.desktop_video.preview_image.src
      }

      // Fallback to mobile poster
      if (block.mobile_video?.preview_image?.src) {
        return block.mobile_video.preview_image.src
      }

      return null
    },
  },
}
</script>

<style lang="scss">
@import './media-blocks-new';
</style>
